import React from "react";
import { signed1, toNum } from "../../../../utils/perfFormat";
import { usePhoneTheme } from "../../useIsPhone";
import { TapPanel, openIndexIn, tapScopeOf, useTapPanel } from "../TapPanel";
import ChartTargets, { columnPlacement } from "./ChartTargets";
import { CHART, FONT, PHONE_CHART_WIDTH, ROLE } from "./chartTokens";

// A rolling line over single games (the team's Form: rolling Net rating):
// the rolling value as a line with the area between it and zero tinted
// bm-orange at 10%, each single game as a small dot (bm-positive at or above
// zero, bm-critical below, clipped at ±clip), a heavier zero line, signed
// gridlines every 10 (20 on a wide range), a marker on the last point and
// the first and last dates under the plot.
//
//   TrendLine({ points, clip = 40, format = signed1, ariaLabel, renderDetail, label, lineColor, phone })
//
// points  [{ key, label, value, rolling, ariaLabel }]: `label` is the date
//         ("May 15"), `value` the single game, `rolling` the rolling value.
// renderDetail(i) opens the point's detail: a HoverCard on hover or focus of
//   its slot (desktop), a readout above the chart after a tap (phone).
// Desktop is a 560 x 200 SVG; phones a 306 x 180 one (the phone card's
// width, so its 10px labels read 10px).
// `lineColor` is the trend ink by default (B18).

const SIZES = {
  desktop: { W: 560, H: 200, L: 30, R: 12, T: 12, B: 22, font: 9.5, dot: 2.4, end: 4.5 },
  phone: { W: PHONE_CHART_WIDTH, H: 180, L: 30, R: 10, T: 12, B: 22, font: 10, dot: 2.6, end: 4.5 },
};

export const trendRange = (points, clip = 40) => {
  const rolling = points.map((p) => toNum(p.rolling)).filter((v) => v !== null);
  const singles = points.map((p) => toNum(p.value)).filter((v) => v !== null);
  const lo = Math.min(-5, ...rolling, ...singles.map((v) => Math.max(v, -clip)));
  const hi = Math.max(5, ...rolling, ...singles.map((v) => Math.min(v, clip)));
  return { lo, hi, step: hi - lo > 60 ? 20 : 10 };
};

export const TrendLine = ({ points = [], clip = 40, format = signed1, ariaLabel, renderDetail, label = "Games", lineColor = ROLE.trend, phone: phoneProp }) => {
  const themePhone = usePhoneTheme();
  const phone = phoneProp ?? themePhone;
  // New points (another team or season) close the readout.
  const tap = useTapPanel(tapScopeOf(points));
  if (!points.length) return null;
  const s = phone ? SIZES.phone : SIZES.desktop;
  const { lo, hi, step } = trendRange(points, clip);
  const n = points.length;
  const span = (s.W - s.L - s.R) / Math.max(n - 1, 1);
  const x = (i) => (n === 1 ? (s.L + s.W - s.R) / 2 : s.L + i * span);
  const y = (v) => s.T + ((hi - Math.max(lo, Math.min(hi, v))) / (hi - lo)) * (s.H - s.T - s.B);
  const grid = [];
  for (let g = Math.ceil(lo / step) * step; g <= hi; g += step) grid.push(g);

  const rolled = points.map((p, i) => ({ i, v: toNum(p.rolling) })).filter((p) => p.v !== null);
  const line = rolled.map((p, j) => `${j ? "L" : "M"}${x(p.i).toFixed(1)},${y(p.v).toFixed(1)}`).join(" ");
  const area =
    rolled.length > 1
      ? `${line} L${x(rolled[rolled.length - 1].i).toFixed(1)},${y(0).toFixed(1)} L${x(rolled[0].i).toFixed(1)},${y(0).toFixed(1)} Z`
      : null;
  const last = rolled[rolled.length - 1];
  const openIndex = phone ? openIndexIn(tap, n) : null;

  const targets = points.map((p, i) => {
    const left = Math.max(0, x(i) - span / 2);
    const right = Math.min(s.W, x(i) + span / 2);
    return {
      key: p.key ?? i,
      label: p.ariaLabel || `${p.label}: ${format(p.value)}`,
      placement: columnPlacement(i, n, "top"),
      style: {
        left: `${(left / s.W) * 100}%`,
        width: `${(Math.max(right - left, 1) / s.W) * 100}%`,
        top: `${(s.T / s.H) * 100}%`,
        height: `${((s.H - s.T - s.B) / s.H) * 100}%`,
      },
    };
  });

  return (
    <div className="flex w-full flex-col">
      {phone && renderDetail && (
        <TapPanel id={tap.panelId} open={openIndex !== null} onClose={tap.close} placement="above">
          {openIndex !== null ? renderDetail(openIndex) : null}
        </TapPanel>
      )}
      <div className="relative w-full">
        <svg viewBox={`0 0 ${s.W} ${s.H}`} role="img" aria-label={ariaLabel || label} className="block h-auto w-full">
          {grid.map((g) => (
            <g key={`grid-${g}`}>
              <line x1={s.L} x2={s.W - s.R} y1={y(g)} y2={y(g)} stroke={CHART.border} strokeWidth={g === 0 ? 1.6 : 1} />
              <text x={s.L - 5} y={y(g) + 3} textAnchor="end" fontFamily={FONT.num} fontSize={s.font} fill={CHART.muted}>
                {g > 0 ? `+${g}` : g < 0 ? `−${Math.abs(g)}` : "0"}
              </text>
            </g>
          ))}
          {area && <path d={area} fill={CHART.orange} fillOpacity={0.1} />}
          {points.map((p, i) => {
            const v = toNum(p.value);
            if (v === null) return null;
            return (
              <circle
                key={`dot-${p.key ?? i}`}
                cx={x(i)}
                cy={y(v)}
                r={openIndex === i ? s.dot + 1.6 : s.dot}
                fill={v >= 0 ? ROLE.better : ROLE.worse}
                fillOpacity={openIndex === i ? 1 : 0.55}
              />
            );
          })}
          {rolled.length > 1 && <path d={line} fill="none" stroke={lineColor} strokeWidth={2.4} strokeLinejoin="round" strokeLinecap="round" />}
          {last && <circle cx={x(last.i)} cy={y(last.v)} r={s.end} fill={lineColor} />}
          <text x={s.L} y={s.H - 6} fontFamily={FONT.num} fontSize={s.font} fill={CHART.muted}>
            {points[0].label}
          </text>
          {n > 1 && (
            <text x={s.W - s.R} y={s.H - 6} textAnchor="end" fontFamily={FONT.num} fontSize={s.font} fill={CHART.muted}>
              {points[n - 1].label}
            </text>
          )}
        </svg>
        {renderDetail && <ChartTargets targets={targets} renderDetail={renderDetail} phone={phone} tap={tap} label={label} />}
      </div>
    </div>
  );
};

export default TrendLine;
